import React from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import { TextField, Button } from '@avangard/ui/core';

import { Hint } from '@modules/layout/atoms';
import { useEnqueueStacks } from '@modules/layout/hooks';
import { FormStyled } from '@modules/layout/styled';
import { GetSchoolQuery, SendSchoolAccessMutation } from '@modules/schools/graphql';

import type { SchoolEntity } from '@modules/schools/entities';
import type {
    SendSchoolAccessMutationType,
    SendSchoolAccessMutationVariables,
} from '@modules/types/graphql';

type SchoolGeneralAccessProps = {
    school: SchoolEntity;
};

const SchoolGeneralAccess = (props: SchoolGeneralAccessProps): React.ReactElement => {
    const { school } = props;

    const { t } = useTranslation(['common', 'access']);

    const { enqueueSuccess, enqueueError } = useEnqueueStacks();

    const [values] = React.useState({
        username: school.attachedUser.username,
        email: school.email,
    });

    const [sendSchoolAccess, { loading: sendSchoolAccessLoading }] = useMutation<
        SendSchoolAccessMutationType,
        SendSchoolAccessMutationVariables
    >(SendSchoolAccessMutation);

    const handleSubmit = async (): Promise<void> => {
        try {
            const { data: sendSchoolAccessData } = await sendSchoolAccess({
                variables: { id: school.id },
                refetchQueries: [{ query: GetSchoolQuery, variables: { id: school.id } }],
                awaitRefetchQueries: true,
            });

            if (sendSchoolAccessData?.sendSchoolAccess) {
                enqueueSuccess(t('access:notifiers.sent.success'));
            } else {
                enqueueError(t('access:notifiers.sent.error'));
            }
        } catch (e) {
            throw e;
        }
    };

    const defaultHintTranslation = t('access:hints.default');

    const hintText = school.isAccessSent
        ? `${defaultHintTranslation} ${t('access:hints.last_sent', {
              date: school.getFormattedAccessSentDate(),
          })}`
        : defaultHintTranslation;

    return (
        <FormStyled.FormColumn>
            <FormStyled.FormColumnTitle>{t('access:titles.access')}</FormStyled.FormColumnTitle>

            <TextField
                disabled
                fullWidth
                id='username'
                label={t('access:form.labels.username')}
                value={values.username}
            />

            <TextField
                disabled
                fullWidth
                id='email'
                label={t('common:forms.labels.email')}
                tooltip={t('access:form.tooltip.email') ?? ''}
                value={values.email}
            />

            <FormStyled.FormRowSubmit>
                <Button variant='outlined' loading={sendSchoolAccessLoading} onClick={handleSubmit}>
                    {t('access:actions.sent')}
                </Button>
            </FormStyled.FormRowSubmit>

            <FormStyled.FormRow>
                <Hint>{hintText}</Hint>
            </FormStyled.FormRow>
        </FormStyled.FormColumn>
    );
};

export { SchoolGeneralAccess };
